interface AlertData {
  type: "warning" | "info" | "error";
  message: string;
  details?: string[];
}

interface QuickStats {
  total_visits: number;
  upcoming_visits: number;
  pending_labs: number;
  active_treatments: number;
  last_visit_date?: string;
  next_visit_date?: string;
}

interface RecentActivity {
  id: number | string;
  type: string;
  title: string;
  date: string;
  status: string;
  details?: any;
}

export interface PatientOverviewData {
  alerts: AlertData[];
  quickStats: QuickStats;
  recentActivity: RecentActivity[];
}

export interface TimelineItem {
  id: string;
  type: 'appointment' | 'prescription' | 'lab_test' | 'treatment_plan';
  date: string;
  title: string;
  description: string;
  details: any;
}

export interface PatientStats {
  total_appointments: number;
  completed_appointments: number;
  upcoming_appointments: number;
  total_prescriptions: number;
  active_prescriptions: number;
  total_lab_requisitions: number;
  pending_lab_results: number;
  total_treatment_plans: number;
  active_treatment_plans: number;
  total_documents: number;
  unreviewed_documents: number;
  vital_signs_count: number;
  last_visit?: string;
  next_appointment?: string;
}

class PatientOverviewService {
  private static BASE_URL = process.env.REACT_APP_API_URL || 'https://dr-bassam-elayoubi.caduceuslb.com/api';

  private static async request(url: string, options: RequestInit = {}): Promise<any> {
    const token = localStorage.getItem('auth_token');
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    const response = await fetch(`${this.BASE_URL}${url}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Get complete patient overview data
   */
  static async getPatientOverview(patientId: number): Promise<PatientOverviewData> {
    const result = await this.request(`/patient-overview/${patientId}`);
    return result.data;
  }

  /**
   * Get patient medical timeline
   */
  static async getPatientTimeline(patientId: number): Promise<TimelineItem[]> {
    const result = await this.request(`/patient-overview/${patientId}/timeline`);
    return result.data;
  }

  /**
   * Get patient quick statistics
   */
  static async getPatientStats(patientId: number): Promise<PatientStats> {
    const result = await this.request(`/patient-overview/${patientId}/quick-stats`);
    return result.data;
  }

  /**
   * Get patient alerts
   */
  static async getPatientAlerts(patientId: number): Promise<AlertData[]> {
    const result = await this.request(`/patient-overview/${patientId}/alerts`);
    return result.data;
  }

  /**
   * Helper method to format activity icons
   */
  static getActivityIcon(type: string): string {
    switch (type) {
      case 'appointment':
        return '📅';
      case 'prescription':
        return '💊';
      case 'lab_order':
      case 'lab_test':
        return '🧪';
      case 'treatment':
      case 'treatment_plan':
        return '📋';
      case 'vital_signs':
        return '❤️';
      case 'document':
        return '📄';
      default:
        return '📝';
    }
  }

  /**
   * Helper method to get status colors
   */
  static getStatusColor(status: string): string {
    switch (status.toLowerCase()) {
      case 'completed':
        return '#10b981'; // Green
      case 'active':
      case 'scheduled':
      case 'confirmed':
        return '#3b82f6'; // Blue
      case 'pending':
      case 'issued':
        return '#f59e0b'; // Yellow
      case 'cancelled':
        return '#ef4444'; // Red
      case 'reviewed':
        return '#6366f1'; // Indigo
      default:
        return '#6b7280'; // Gray
    }
  }

  /**
   * Helper method to format dates
   */
  static formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  /**
   * Helper method to format relative time
   */
  static getRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) {
      return 'Today';
    } else if (diffInDays === 1) {
      return 'Yesterday';
    } else if (diffInDays < 7) {
      return `${diffInDays} days ago`;
    } else if (diffInDays < 30) {
      return `${Math.floor(diffInDays / 7)} weeks ago`;
    } else if (diffInDays < 365) {
      return `${Math.floor(diffInDays / 30)} months ago`;
    } else {
      return `${Math.floor(diffInDays / 365)} years ago`;
    }
  }

  /**
   * Helper method to get alert priority styling
   */
  static getAlertStyling(type: string): { backgroundColor: string; borderColor: string; color: string } {
    switch (type) {
      case 'error':
        return {
          backgroundColor: '#fee2e2',
          borderColor: '#ef4444',
          color: '#dc2626'
        };
      case 'warning':
        return {
          backgroundColor: '#fef3c7',
          borderColor: '#f59e0b',
          color: '#92400e'
        };
      case 'info':
        return {
          backgroundColor: '#dbeafe',
          borderColor: '#3b82f6',
          color: '#1e40af'
        };
      default:
        return {
          backgroundColor: '#f3f4f6',
          borderColor: '#6b7280',
          color: '#374151'
        };
    }
  }

  /**
   * Helper method to check if a date is upcoming
   */
  static isUpcoming(dateString: string, daysAhead: number = 7): boolean {
    const date = new Date(dateString);
    const now = new Date();
    const futureDate = new Date(now.getTime() + (daysAhead * 24 * 60 * 60 * 1000));
    
    return date >= now && date <= futureDate;
  }

  /**
   * Helper method to check if a date is overdue
   */
  static isOverdue(dateString: string): boolean {
    const date = new Date(dateString);
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Set to start of today
    
    return date < now;
  }

  /**
   * Helper method to group activities by date
   */
  static groupActivitiesByDate(activities: RecentActivity[]): Record<string, RecentActivity[]> {
    const grouped: Record<string, RecentActivity[]> = {};
    
    activities.forEach(activity => {
      const dateKey = this.formatDate(activity.date);
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(activity);
    });
    
    return grouped;
  }

  /**
   * Helper method to calculate health score based on patient data
   */
  static calculateHealthScore(overview: PatientOverviewData): {
    score: number;
    level: 'excellent' | 'good' | 'fair' | 'poor';
    factors: string[];
  } {
    let score = 100;
    const factors: string[] = [];

    // Deduct for alerts
    const criticalAlerts = overview.alerts.filter(a => a.type === 'error').length;
    const warningAlerts = overview.alerts.filter(a => a.type === 'warning').length;
    
    score -= (criticalAlerts * 20);
    score -= (warningAlerts * 10);
    
    if (criticalAlerts > 0) factors.push(`${criticalAlerts} critical alert(s)`);
    if (warningAlerts > 0) factors.push(`${warningAlerts} warning(s)`);

    // Deduct for pending labs
    if (overview.quickStats.pending_labs > 0) {
      score -= (overview.quickStats.pending_labs * 5);
      factors.push(`${overview.quickStats.pending_labs} pending lab result(s)`);
    }

    // Add for active treatments (shows proactive care)
    if (overview.quickStats.active_treatments > 0) {
      score += (overview.quickStats.active_treatments * 5);
    }

    // Deduct if no recent activity
    const recentActivityCount = overview.recentActivity.filter(activity => 
      this.isWithinDays(activity.date, 30)
    ).length;
    
    if (recentActivityCount === 0) {
      score -= 15;
      factors.push('No recent medical activity');
    }

    // Ensure score is between 0 and 100
    score = Math.max(0, Math.min(100, score));

    let level: 'excellent' | 'good' | 'fair' | 'poor';
    if (score >= 90) level = 'excellent';
    else if (score >= 70) level = 'good';
    else if (score >= 50) level = 'fair';
    else level = 'poor';

    return { score, level, factors };
  }

  /**
   * Helper method to check if date is within specified days
   */
  private static isWithinDays(dateString: string, days: number): boolean {
    const date = new Date(dateString);
    const now = new Date();
    const daysDiff = (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24);
    return daysDiff <= days;
  }
}

export default PatientOverviewService;